import React, { useEffect, useRef } from 'react';

/* ===== ADVANCED PARTICLE NETWORK - Interactive constellation with mouse ===== */
export function ParticleNetwork() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let mouse = { x: null, y: null };
    let particles = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.8;
        this.baseX = this.x;
        this.baseY = this.y;
        this.vx = (Math.random() - 0.5) * 0.4;
        this.vy = (Math.random() - 0.5) * 0.4;
        this.opacity = Math.random() * 0.7 + 0.4;
        this.pulseSpeed = Math.random() * 0.02 + 0.01;
        this.pulseOffset = Math.random() * Math.PI * 2;
      }
      update(time) {
        // Gentle drift
        this.x += this.vx;
        this.y += this.vy;
        if (this.x < 0 || this.x > canvas.width) this.vx *= -1;
        if (this.y < 0 || this.y > canvas.height) this.vy *= -1;

        // Mouse attraction/repulsion
        if (mouse.x !== null) {
          const dx = this.x - mouse.x;
          const dy = this.y - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 180) {
            const force = (180 - dist) / 180;
            this.x += (dx / dist) * force * 1.5;
            this.y += (dy / dist) * force * 1.5;
          }
        }

        // Pulse size
        this.currentSize = this.size + Math.sin(time * this.pulseSpeed + this.pulseOffset) * 0.5;
      }
      draw(time) {
        const pulse = 0.5 + Math.sin(time * this.pulseSpeed + this.pulseOffset) * 0.3;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.currentSize, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(160, 170, 255, ${this.opacity * pulse})`;
        ctx.fill();

        // Glow
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.currentSize * 4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(129, 140, 248, ${this.opacity * pulse * 0.15})`;
        ctx.fill();
      }
    }

    const isMobile = window.innerWidth < 768;
    const count = isMobile ? 30 : Math.min(80, Math.floor(canvas.width * canvas.height / 15000));
    for (let i = 0; i < count; i++) particles.push(new Particle());

    function connectParticles() {
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 140) {
            const opacity = 0.2 * (1 - dist / 140);
            ctx.beginPath();
            ctx.strokeStyle = `rgba(160, 170, 255, ${opacity})`;
            ctx.lineWidth = 0.7;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Connect to mouse
      if (mouse.x !== null) {
        particles.forEach(p => {
          const dx = p.x - mouse.x;
          const dy = p.y - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 200) {
            const opacity = 0.2 * (1 - dist / 200);
            ctx.beginPath();
            ctx.strokeStyle = `rgba(167, 139, 250, ${opacity})`;
            ctx.lineWidth = 0.8;
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(mouse.x, mouse.y);
            ctx.stroke();
          }
        });
      }
    }

    let time = 0;
    function animate() {
      time++;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => { p.update(time); p.draw(time); });
      connectParticles();
      animId = requestAnimationFrame(animate);
    }
    animate();

    const onMouse = e => { mouse.x = e.clientX; mouse.y = e.clientY; };
    const onLeave = () => { mouse.x = null; mouse.y = null; };
    document.addEventListener('mousemove', onMouse);
    document.addEventListener('mouseleave', onLeave);
    window.addEventListener('resize', resize);

    return () => {
      cancelAnimationFrame(animId);
      document.removeEventListener('mousemove', onMouse);
      document.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="particle-network-canvas" />;
}

/* ===== FLOATING HEX CODE ===== */
export function FloatingCode() {
  return (
    <>
      <div className="floating-code fc-1">root@kali:~# nmap -sV --script=vuln</div>
      <div className="floating-code fc-2">0x4F53494E54 // ENCRYPTED</div>
      <div className="floating-code fc-3">CVE-2024-XXXX // PATCHED</div>
      <div className="floating-code fc-4">ssh-rsa AAAA...EtHiCaL_HaCk</div>
    </>
  );
}

/* ===== CYBER SHIELD (3D Rotating) ===== */
export function CyberShield() {
  return (
    <div className="cyber-shield-scene">
      <div className="shield-3d">
        <div className="orbit-ring"><div className="orbit-dot" /></div>
        <div className="orbit-ring ring-b"><div className="orbit-dot" /></div>
        <div className="orbit-ring ring-c"><div className="orbit-dot" /></div>
        <div className="shield-inner">
          <svg viewBox="0 0 200 220" fill="none" className="shield-svg">
            <path d="M100 10 L185 50 L185 120 C185 170 145 200 100 215 C55 200 15 170 15 120 L15 50 Z"
              fill="rgba(129,140,248,0.12)" stroke="rgba(129,140,248,0.7)" strokeWidth="2.5"/>
            <path d="M100 30 L170 60 L170 115 C170 158 138 185 100 198 C62 185 30 158 30 115 L30 60 Z"
              fill="rgba(129,140,248,0.06)" stroke="rgba(129,140,248,0.4)" strokeWidth="1.5"/>
            <rect x="78" y="95" width="44" height="35" rx="4" fill="none" stroke="rgba(129,140,248,0.8)" strokeWidth="2.5"/>
            <path d="M88 95 V82 C88 72 95 65 100 65 C105 65 112 72 112 82 V95"
              fill="none" stroke="rgba(129,140,248,0.8)" strokeWidth="2.5" strokeLinecap="round"/>
            <circle cx="100" cy="108" r="5" fill="rgba(129,140,248,0.8)"/>
            <rect x="98" y="112" width="4" height="8" rx="2" fill="rgba(129,140,248,0.8)"/>
            <text x="100" y="155" textAnchor="middle" fill="rgba(129,140,248,0.5)"
              fontFamily="JetBrains Mono, monospace" fontSize="11" fontWeight="600">CERTIFIED</text>
            <text x="100" y="170" textAnchor="middle" fill="rgba(129,140,248,0.7)"
              fontFamily="JetBrains Mono, monospace" fontSize="13" fontWeight="700">ETHICAL HACKER</text>
          </svg>
        </div>
      </div>
    </div>
  );
}

/* ===== TERMINAL ===== */
export function TerminalBox() {
  return (
    <div className="terminal-box">
      <div className="terminal-header">
        <div className="terminal-dot red" />
        <div className="terminal-dot yellow" />
        <div className="terminal-dot green" />
        <span className="terminal-title">rajat@kali ~ </span>
      </div>
      <div className="terminal-line">
        <span className="prompt">$ </span>
        <span className="cmd tw tw-1">nmap -sV --script=vuln target.com</span>
      </div>
      <div className="terminal-line">
        <span className="prompt">$ </span>
        <span className="cmd tw tw-2">Found 3 vulnerabilities</span>
      </div>
      <div className="terminal-line">
        <span className="prompt">$ </span>
        <span className="cmd tw tw-3">Generating security report...</span>
        <span className="terminal-cursor" />
      </div>
    </div>
  );
}

/* ===== SCANLINE ===== */
export function Scanline() {
  return <div className="cyber-scanline" />;
}
